import { useQuery } from '@tanstack/react-query';
import NotificationsService, { NotificationUnreadCountModel } from 'services/NotificationsService';
import { useCurrentUserOrNullIfAnonymous } from 'hooks/useCurrentUser';

export const notificationUnreadCountQueryKey = ['notifications', 'unread-count'] as const;

/**
 * Returns the unread notification count for the current user.
 *
 * Polling is delegated to React Query via `refetchInterval` (60s) and refetch
 * on window focus, so we always show a reasonably fresh badge without a manual
 * timer.
 *
 * Returns `undefined` while loading and `{ total: 0, byCategory: {} }` when no
 * user is signed in (so the header bell stays hidden).
 */
export default function useUnreadNotificationCount(): NotificationUnreadCountModel | undefined {
  const currentUser = useCurrentUserOrNullIfAnonymous();
  const enabled = !!currentUser;

  const query = useQuery<NotificationUnreadCountModel>({
    queryKey: notificationUnreadCountQueryKey,
    queryFn: () => NotificationsService.unreadCount(),
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  if (!enabled) {
    return { total: 0, byCategory: {} };
  }
  return query.data;
}
